/**
 * apps/web :: pass :: PassLayout
 *
 * Shared chrome for every pass screen — a phone-ish container with an
 * indigo-accented header showing the active country pack name, an
 * "Offline-ready" badge, and a freshness line, plus a slot for the
 * screen body. Wave-1 screens wrap their content in this so the pass
 * surface stays visually consistent without each screen re-implementing
 * the header.
 *
 * Props:
 *   - country  optional pack id (e.g. "IT"); resolves the pack name +
 *              freshness from `@roadbridge/pass-content`.
 *   - title    optional override for the header heading.
 *   - children the screen body.
 */
import type { ReactNode } from "react";

import { PACKS, type PackId, type TravelerPack } from "@/lib/pass-content";

export interface PassLayoutProps {
  country?: string;
  title?: string;
  children: ReactNode;
}

function resolvePack(country?: string): TravelerPack | undefined {
  if (!country) return undefined;
  const id = country.toUpperCase() as PackId;
  return (PACKS as Record<string, TravelerPack>)[id];
}

/**
 * Latest `lastReviewed` date across the pack's rules — used for the
 * "Updated …" freshness line so travelers can trust the content recency.
 */
function freshness(pack: TravelerPack | undefined): string | null {
  if (!pack) return null;
  const dates = pack.rules
    .map((r) => r.lastReviewed)
    .filter(Boolean)
    .sort();
  return dates.length > 0 ? dates[dates.length - 1] : null;
}

export default function PassLayout({
  country,
  title,
  children,
}: PassLayoutProps): JSX.Element {
  const pack = resolvePack(country);
  const heading = title ?? pack?.jurisdiction.name ?? "Your pass";
  const updated = freshness(pack);

  return (
    <div className="mx-auto min-h-screen w-full max-w-md bg-white">
      <header className="bg-indigo-600 px-md py-md text-white">
        <div className="flex items-start justify-between gap-2">
          <h1 className="font-display text-xl font-bold tracking-tight">
            {heading}
          </h1>
          <span
            className="inline-flex shrink-0 items-center rounded-full bg-white/20 px-2 py-0.5 text-xs font-semibold"
            data-testid="pass-offline-badge"
          >
            Offline-ready
          </span>
        </div>
        {updated ? (
          <p className="mt-1 text-xs text-indigo-100" data-testid="pass-freshness">
            Updated {updated}
          </p>
        ) : null}
      </header>
      <main className="px-md py-md">{children}</main>
    </div>
  );
}
